import { parseDate } from '@internationalized/date';
import { CHART_NO_FOCUS_OUTLINE, ChartTooltipContent, DEFAULT_CHART_HEIGHT } from '@orn/shared-web/components/Chart';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import AnimationFixToolTip from '@orn/shared-web/components/Tooltip/AnimationFixToolTip';
import * as React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { calculateDailyBalances } from '../../../utils/balances';
import { formatChartPeriodLong, formatChartPeriodShort, formatMoneyShort } from '../../../utils/formatting';
import { useDateRangeDisplayText, type CalendarDateRange } from '../../DateRangePicker';
import { ChartCard } from '../ChartCard';
import { ChartHeader } from '../ChartHeader';
import { calculateXAxisTicks } from '../utils';

import type { ColumnTable } from 'arquero';

export interface IBalancesChartProps {
  className?: string;
  transactions: ColumnTable | null;
  currentBalance: number;
  dateRange: CalendarDateRange;
  isLoading?: boolean;
}

const BalancesChart = ({
  className = '',
  transactions,
  currentBalance,
  dateRange,
  isLoading = false,
}: IBalancesChartProps) => {
  const { containerWidth, containerRef } = useContainerWidth();
  const dateRangeDisplayText = useDateRangeDisplayText(dateRange);

  // Calculate daily balances from transactions
  const balances = React.useMemo(() => {
    return calculateDailyBalances(transactions, currentBalance, dateRange.start);
  }, [transactions, currentBalance, dateRange.start]);

  // Process the data
  const chartData = React.useMemo(() => {
    if (!balances || balances.length === 0 || !dateRange) {
      return [];
    }

    return balances
      .filter((b) => {
        // Parse date string to CalendarDate for comparison
        const balanceDate = parseDate(b.date);
        return balanceDate.compare(dateRange.start) >= 0 && balanceDate.compare(dateRange.end) <= 0;
      })
      .map((b) => ({
        period: b.date,
        balance: b.balance,
      }));
  }, [balances, dateRange]);

  const xAxisTicks = React.useMemo(() => {
    return calculateXAxisTicks({
      periods: chartData.map((d) => d.period),
      containerWidth,
      dateRange,
    });
  }, [chartData, containerWidth, dateRange]);

  return (
    <ChartCard
      containerRef={containerRef}
      className={className}
      header={<ChartHeader title="Balance history" subtitle={dateRangeDisplayText} />}
    >
      {isLoading ? (
        <div className="flex items-center justify-center" style={{ height: `${DEFAULT_CHART_HEIGHT}px` }}>
          <LoadingIndicator size="md" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="text-tertiary flex h-full items-center justify-center">
          No balance data in selected date range
        </div>
      ) : (
        <div style={{ width: '100%', height: `${DEFAULT_CHART_HEIGHT}px` }}>
          <ResponsiveContainer width="100%" height={DEFAULT_CHART_HEIGHT} className={CHART_NO_FOCUS_OUTLINE}>
            <AreaChart
              data={chartData}
              margin={{
                left: -10,
                right: 20,
                top: 5,
                bottom: 5,
              }}
              className="text-tertiary [&_.recharts-text]:text-xs"
            >
              <defs>
                <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="currentColor" className="text-utility-brand-600" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="currentColor" className="text-utility-brand-600" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                vertical={false}
                horizontal={true}
                stroke="currentColor"
                strokeDasharray="0"
                className="text-utility-gray-100"
              />

              <XAxis
                fill="currentColor"
                dataKey="period"
                axisLine={false}
                tickLine={false}
                tickMargin={12}
                ticks={xAxisTicks}
                interval={0}
                tickFormatter={(value: string) => formatChartPeriodShort(value, 'day', dateRange.end.year)}
              />

              <YAxis
                fill="currentColor"
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                width={80}
                tickFormatter={(value: number) => formatMoneyShort(value, { precision: 'whole' })}
              />

              <AnimationFixToolTip
                content={<ChartTooltipContent />}
                formatter={(value: number) => {
                  return `$${Number(value).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}`;
                }}
                labelFormatter={(value: string) => formatChartPeriodLong(value, 'day')}
                cursor={{
                  className: 'stroke-utility-brand-600 stroke-2',
                }}
              />

              <Area
                isAnimationActive={false}
                dataKey="balance"
                name="Balance"
                type="monotone"
                stroke="currentColor"
                strokeWidth={2}
                className="text-utility-brand-600"
                fill="url(#balanceGradient)"
                fillOpacity={1}
                activeDot={{
                  className: 'fill-bg-primary stroke-utility-brand-600 stroke-2',
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};

export default BalancesChart;
